import { apiGet, apiPost, apiPut, apiDelete } from '@/shared/api/apiClient';
import type { PagedResult } from '@/types';

// DTOs matching backend
export interface LocationListDto {
  id: string;
  name: string;
  code?: string;
  locationTypeId: string;
  locationTypeValue: string;
  city: string;
  state: string;
  country: string;
  isActive: boolean;
  hasGeofencing: boolean;
  latitude?: number;
  longitude?: number;
  geofenceRadiusMeters?: number;
  parentLocationId?: string;
  parentLocationName?: string;
}

export interface CreateLocationRequest {
  name: string;
  code?: string;
  description?: string;
  locationTypeId: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  latitude?: number;
  longitude?: number;
  geofenceRadiusMeters?: number;
  isActive: boolean;
  parentLocationId?: string;
}

export interface UpdateLocationRequest {
  name: string;
  code?: string;
  description?: string;
  locationTypeId: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  country: string;
  postalCode: string;
  latitude?: number;
  longitude?: number;
  geofenceRadiusMeters?: number;
  isActive: boolean;
  parentLocationId?: string;
}

// API functions
export async function getAllLocations(tenantId: string): Promise<LocationListDto[]> {
  return apiGet<LocationListDto[]>(`/locations?tenantId=${tenantId}`);
}

export interface LocationListParams {
  tenantId: string;
  /** 0-based row offset. */
  offset?: number;
  limit?: number;
  search?: string;
}

export interface LocationListResult {
  items: LocationListDto[];
  totalCount: number;
}

/**
 * Server-side paginated/searchable variant of `getAllLocations`, used by the
 * Locations Configuration screen. Only appends params that are actually set —
 * `offset: 0` is falsy but valid, so this checks `!== undefined`, not truthiness.
 */
export async function getLocationsPaged(params: LocationListParams): Promise<LocationListResult> {
  const query = new URLSearchParams({ tenantId: params.tenantId });
  if (params.offset !== undefined) query.append('offset', String(params.offset));
  if (params.limit !== undefined) query.append('limit', String(params.limit));
  if (params.search) query.append('search', params.search);

  const data = await apiGet<PagedResult<LocationListDto>>(`/locations?${query.toString()}`);
  return { items: data.items ?? [], totalCount: data.totalCount ?? 0 };
}

export async function getActiveLocations(tenantId: string): Promise<LocationListDto[]> {
  return apiGet<LocationListDto[]>(`/locations/active?tenantId=${tenantId}`);
}

export async function getDeletedLocations(tenantId: string): Promise<LocationListDto[]> {
  return apiGet<LocationListDto[]>(`/locations/deleted?tenantId=${tenantId}`);
}

export async function getChildLocations(parentId: string, tenantId: string): Promise<LocationListDto[]> {
  return apiGet<LocationListDto[]>(`/locations/${parentId}/children?tenantId=${tenantId}`);
}

export async function createLocation(
  data: CreateLocationRequest,
  tenantId: string,
  userId: string
): Promise<{ id: string }> {
  return apiPost<{ id: string }>(`/locations?tenantId=${tenantId}&userId=${userId}`, data);
}

export async function updateLocation(
  id: string,
  data: UpdateLocationRequest,
  tenantId: string,
  userId: string
): Promise<void> {
  return apiPut<void>(`/locations/${id}?tenantId=${tenantId}&userId=${userId}`, data);
}

export async function deleteLocation(id: string, tenantId: string, userId: string): Promise<void> {
  return apiDelete<void>(`/locations/${id}?tenantId=${tenantId}&userId=${userId}`);
}

export async function restoreLocation(id: string, tenantId: string, userId: string): Promise<void> {
  return apiPost<void>(`/locations/${id}/restore?tenantId=${tenantId}&userId=${userId}`, {});
}

export async function activateLocation(id: string, tenantId: string, userId: string): Promise<void> {
  return apiPost<void>(`/locations/${id}/activate?tenantId=${tenantId}&userId=${userId}`, {});
}

export async function deactivateLocation(id: string, tenantId: string, userId: string): Promise<void> {
  return apiPost<void>(`/locations/${id}/deactivate?tenantId=${tenantId}&userId=${userId}`, {});
}

// Geofence validation for attendance
export interface ValidateGeofenceRequest {
  latitude: number;
  longitude: number;
}

export interface ValidateGeofenceResponse {
  isWithinGeofence: boolean;
  locationType: 'office' | 'remote';
  locationId?: string;
  locationName?: string;
  distanceMeters?: number;
}

export const validateGeofence = async (
  latitude: number,
  longitude: number,
  tenantId: string
): Promise<ValidateGeofenceResponse> => {
  const payload: ValidateGeofenceRequest = { latitude, longitude };
  return apiPost<ValidateGeofenceResponse>(`/locations/validate-geofence?tenantId=${tenantId}`, payload);
};
